import axios from 'axios';
import type { FilterState, PaginatedResponse, TimelineEvent, FilterPreset } from '../types';

// In development, Vite proxies /api → localhost:8787 (worker dev server).
// In production, VITE_API_URL points to the deployed Cloudflare Worker.
const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Token provider set by AuthProvider once MSAL has a valid token
let getToken: (() => Promise<string>) | null = null;

export function setAuthTokenProvider(provider: () => Promise<string>) {
  getToken = provider;
}

// Attach live MSAL token to every request
api.interceptors.request.use(async (config) => {
  if (getToken) {
    const token = await getToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export async function fetchEvents(
  filters: FilterState,
  limit = 50,
  offset = 0
): Promise<PaginatedResponse<TimelineEvent>> {
  const params: Record<string, string | number | boolean> = { limit, offset };
  if (filters.mailbox) params.mailbox = filters.mailbox;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.senders) params.senders = filters.senders;
  if (filters.subject) params.subject = filters.subject;
  if (filters.conversationId) params.conversationId = filters.conversationId;
  if (filters.folder) params.folder = filters.folder;
  if (filters.sensitivity) params.sensitivity = filters.sensitivity;
  if (filters.types) params.types = filters.types;
  if (filters.hasAttachment !== undefined) params.hasAttachment = filters.hasAttachment;

  const res = await api.get<PaginatedResponse<TimelineEvent>>('/events', { params });
  return res.data;
}

export async function fetchAllEventsForPrint(filters: FilterState): Promise<TimelineEvent[]> {
  const params: Record<string, string | number | boolean> = { limit: 5000, offset: 0 };
  if (filters.mailbox) params.mailbox = filters.mailbox;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.senders) params.senders = filters.senders;
  if (filters.subject) params.subject = filters.subject;
  if (filters.conversationId) params.conversationId = filters.conversationId;
  if (filters.folder) params.folder = filters.folder;
  if (filters.sensitivity) params.sensitivity = filters.sensitivity;
  if (filters.types) params.types = filters.types;
  if (filters.hasAttachment !== undefined) params.hasAttachment = filters.hasAttachment;

  const res = await api.get<PaginatedResponse<TimelineEvent>>('/events', { params });
  return res.data.data;
}

export async function fetchEvent(id: string): Promise<TimelineEvent> {
  const res = await api.get<{ data: TimelineEvent }>(`/events/${id}`);
  return res.data.data;
}

export async function pinEvent(id: string): Promise<void> {
  await api.patch(`/events/${id}/pin`);
}

export async function saveFilterPreset(
  name: string,
  filters: FilterState,
  owner = 'me'
): Promise<FilterPreset> {
  const res = await api.post<{ data: FilterPreset }>('/filters', { name, owner, filters });
  return res.data.data;
}

export async function loadFilterPresets(owner = 'me'): Promise<FilterPreset[]> {
  const res = await api.get<{ data: FilterPreset[] }>('/filters', { params: { owner } });
  return res.data.data;
}

export function buildExportUrl(filters: FilterState, format: 'csv' | 'json'): string {
  const params = new URLSearchParams({ format, ...Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== '')
    .reduce((acc, [k, v]) => ({ ...acc, [k]: String(v) }), {}) });
  return `/api/export?${params.toString()}`;
}

export async function triggerManualIngest(mailbox: string, folderName?: string): Promise<{ ingested: number; errors: number }> {
  const res = await api.post<{ data: { ingested: number; errors: number } }>('/ingest/manual', { mailbox, folderName });
  return res.data.data;
}
